import type { ReactNode } from 'react';
import { StatusBadge } from '../ui/StatusBadge';
import type { BadgeVariant } from '../ui/StatusBadge';

/**
 * ConfidenceIndicator — wraps a field value and displays a confidence
 * level badge with visual highlighting based on configurable thresholds.
 *
 * - confidence >= highThreshold: green badge, no highlight
 * - confidence >= lowThreshold and < highThreshold: yellow highlight + "Low Confidence" badge
 * - confidence < lowThreshold: red highlight + "Needs Review" badge
 *
 * Requirements: 57.14, 57.15, 57.16
 */

export interface ConfidenceIndicatorProps {
  /** Confidence score from 0 to 1. */
  confidence: number;
  /** The field content to wrap. */
  children: ReactNode;
  /** Optional field name for accessibility labeling. */
  fieldName?: string;
  /** Threshold at or above which confidence is considered high (default 0.70). */
  highThreshold?: number;
  /** Threshold below which confidence is considered critically low (default 0.40). */
  lowThreshold?: number;
}

export type ConfidenceLevel = 'high' | 'medium' | 'low';

/**
 * Determines the confidence level based on the score and thresholds.
 */
export function getConfidenceLevel(
  confidence: number,
  highThreshold: number,
  lowThreshold: number,
): ConfidenceLevel {
  if (confidence >= highThreshold) return 'high';
  if (confidence >= lowThreshold) return 'medium';
  return 'low';
}

const levelStyles: Record<ConfidenceLevel, { bg: string; border: string }> = {
  high: { bg: 'transparent', border: 'transparent' },
  medium: { bg: '#fef9c3', border: '#facc15' },
  low: { bg: '#fee2e2', border: '#ef4444' },
};

const levelBadge: Record<ConfidenceLevel, { label: string; variant: BadgeVariant }> = {
  high: { label: '', variant: 'green' },
  medium: { label: 'Low Confidence', variant: 'yellow' },
  low: { label: 'Needs Review', variant: 'red' },
};

export function ConfidenceIndicator({
  confidence,
  children,
  fieldName,
  highThreshold = 0.7,
  lowThreshold = 0.4,
}: ConfidenceIndicatorProps) {
  const level = getConfidenceLevel(confidence, highThreshold, lowThreshold);
  const style = levelStyles[level];
  const badge = levelBadge[level];
  const pct = `${Math.round(confidence * 100)}%`;

  const ariaLabel = fieldName
    ? `${fieldName} confidence: ${pct}`
    : `Confidence: ${pct}`;

  return (
    <div
      data-testid="confidence-indicator"
      data-confidence-level={level}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: level === 'high' ? '0' : '0.35rem 0.5rem',
        borderRadius: '4px',
        backgroundColor: style.bg,
        border: level === 'high' ? 'none' : `1px solid ${style.border}`,
      }}
      aria-label={ariaLabel}
      role="group"
    >
      <div style={{ flex: 1 }}>{children}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
        {badge.label ? (
          <StatusBadge label={badge.label} variant={badge.variant} />
        ) : (
          <StatusBadge label={pct} variant="green" />
        )}
        {badge.label && (
          <span
            style={{
              fontSize: '0.7rem',
              color: '#64748b',
              whiteSpace: 'nowrap',
            }}
          >
            {pct}
          </span>
        )}
      </div>
    </div>
  );
}
